import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Col, Empty, List, Row, Space, Tag, Typography } from "antd";
import {
  CameraOutlined,
  CheckCircleOutlined,
  FilePdfOutlined,
  LockOutlined,
  PlusOutlined,
  TeamOutlined,
  WarningOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import DiaryCalendar from "./DiaryCalendar";
import ReportDialog from "./ReportDialog";
import { dayUrl, formatHours, todayBerlin } from "./format";

/**
 * One project's diary: month calendar with filled and missing days, the month's entries and the
 * PDF of a period. Used on /user/site-diary and as the tab on the project page.
 * `can` comes from the meta ({ create, manage }); `clientEmail` prefills the PDF mail.
 */
const ProjectDiary = ({ project, can = {}, clientEmail = "" }) => {
  const { t } = useTranslation("dashboard");
  const history = useHistory();
  const [month, setMonth] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);

  const open = date => history.push(dayUrl(project, date));
  const entries = month?.data || [];

  return (
    <div className="sdi-project">
      <Space wrap className="sdi-project-actions">
        {can.create && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => open(todayBerlin())}>
            {t("Today's entry")}
          </Button>
        )}
        <Button icon={<FilePdfOutlined />} onClick={() => setReportOpen(true)}>
          {t("PDF / send")}
        </Button>
      </Space>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <DiaryCalendar project={project} onSelect={date => open(date)} onLoaded={setMonth} />
        </Col>
        <Col xs={24} lg={14}>
          {month?.missing?.length > 0 && (
            <div className="sdi-missing-line">
              <Typography.Text type="danger">
                <WarningOutlined /> {t("Missing entries")}:
              </Typography.Text>{" "}
              {month.missing.map(day => (
                <Tag key={day} color="error" className="sdi-day-tag" onClick={() => open(day)}>
                  {dayjs(day).format("DD.MM.")}
                </Tag>
              ))}
            </div>
          )}
          {entries.length ? (
            <List
              className="sdi-entry-list"
              dataSource={entries}
              renderItem={entry => (
                <List.Item className="sdi-entry-item" onClick={() => open(entry.date)}>
                  <List.Item.Meta
                    title={
                      <Space size={6} wrap>
                        <Typography.Text strong>
                          {dayjs(entry.date).format("dd, DD.MM.YYYY")}
                        </Typography.Text>
                        {entry.conditions && (
                          <Typography.Text type="secondary">
                            {entry.conditions}
                            {entry.temperatureNoon !== null ? `, ${entry.temperatureNoon} °C` : ""}
                          </Typography.Text>
                        )}
                        {entry.signed ? (
                          <Tag color="success" icon={<CheckCircleOutlined />}>
                            {t("Signed")}
                          </Tag>
                        ) : entry.locked ? (
                          <Tag icon={<LockOutlined />}>{t("Locked")}</Tag>
                        ) : null}
                        {entry.obstructions > 0 && (
                          <Tag color="warning" icon={<WarningOutlined />}>
                            {entry.obstructions}
                          </Tag>
                        )}
                      </Space>
                    }
                    description={
                      <>
                        {entry.summary && <div className="sdi-clamp">{entry.summary}</div>}
                        <Space size={12} wrap className="sdi-small">
                          <span>
                            <TeamOutlined /> {entry.people} · {formatHours(entry.minutes)} {t("h")}
                          </span>
                          {entry.photos > 0 && (
                            <span>
                              <CameraOutlined /> {entry.photos}
                            </span>
                          )}
                          {entry.authors.length > 0 && <span>{entry.authors.join(", ")}</span>}
                        </Space>
                      </>
                    }
                  />
                </List.Item>
              )}
            />
          ) : (
            month && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t("No diary entries in this month.")}
              />
            )
          )}
        </Col>
      </Row>
      <ReportDialog
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        project={project}
        defaultEmail={clientEmail || month?.project?.clientEmail || ""}
        canEmail={can.manage}
      />
    </div>
  );
};

export default ProjectDiary;
